import * as React from "react";
import { Input, Popover, Tag } from "antd";
import { IS_DEPLOYMENT_ENV_PROD, CONSOLE_URL } from "../../../../envVariables";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectOrgs } from "../../../../store/userDataSlice";
import { selectContext, setOrg } from "../../../../store/contextSlice";
import { CheckOutlined } from "@ant-design/icons";
import { Org } from "../../../../schema/types/org/entity";

interface Props extends React.PropsWithChildren {
  isOpen: boolean;
  setIsOpen: any;
}
export const ContextOrgDrawer: React.FC<Props> = ({ isOpen, setIsOpen, children }) => {
  const dispatch = useAppDispatch();
  const context = useAppSelector(selectContext);
  const orgs = useAppSelector(selectOrgs);

  const [orgSearch, setOrgSearch] = React.useState<string>("");
  const filterRef = React.useRef(null as any);
  const [programmaticHover, setProgrammaticHover] = React.useState("");

  React.useEffect(() => {
    if (!isOpen) return;
    if (filterRef.current) {
      setTimeout(() => {
        filterRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  const orgItems = React.useMemo(() => {
    return orgs.filter((orgItem) => {
      if (orgSearch && !orgItem.name.toLowerCase().includes(orgSearch.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [orgs, orgSearch]);

  const activePassiveOrderedOrgItems = React.useMemo(() => {
    const activeOrgs: Org[] = [];
    const passiveOrgs: Org[] = [];

    for (const org of orgs) {
      if (orgSearch && !org.name.toLowerCase().includes(orgSearch.toLowerCase())) {
        continue;
      }

      if (!org.status?.active) {
        passiveOrgs.push(org);
      } else {
        activeOrgs.push(org);
      }
    }

    return [...activeOrgs, ...passiveOrgs];
  }, [orgs, orgSearch]);

  React.useEffect(() => {
    if (!orgSearch) {
      setProgrammaticHover("");
      return;
    }

    const firstOrg = orgs.find((orgItem) => orgItem.name.toLowerCase().includes(orgSearch.toLowerCase()));
    if (firstOrg) {
      setProgrammaticHover(firstOrg.name);
    }
  }, [orgItems, orgSearch]);

  React.useEffect(() => {
    const firstItem = activePassiveOrderedOrgItems[0];
    if (!programmaticHover || (firstItem && firstItem.name === programmaticHover)) {
      const el = document.querySelector(`.drawer-filter-section`);
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
      return;
    }
    const el = document.getElementById(`org-item-${programmaticHover}`);
    if (el) {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [programmaticHover]);

  function handleKeyDown(e) {
    const code = e.code;
    if (code === "Escape") {
      if (orgSearch) {
        setOrgSearch("");
      } else {
        setIsOpen(false);
        setTimeout(() => {
          const el = document.querySelector(`.context-widget-org`);
          if (el) {
            (el as any).focus();
          }
        });
      }
    } else if (code === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();

      if (programmaticHover) {
        const index = activePassiveOrderedOrgItems.findIndex((g) => g.name === programmaticHover);
        const nextItem = activePassiveOrderedOrgItems[index + 1];
        if (nextItem) {
          setProgrammaticHover(nextItem.name);
        } else {
          const firstItem = activePassiveOrderedOrgItems[0];
          if (firstItem) {
            setProgrammaticHover(firstItem.name);
          }
        }
      } else if (activePassiveOrderedOrgItems.length > 0) {
        setProgrammaticHover(activePassiveOrderedOrgItems[0].name);
      }
    } else if (code === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();

      if (programmaticHover) {
        const index = activePassiveOrderedOrgItems.findIndex((g) => g.name === programmaticHover);
        const previousItem = activePassiveOrderedOrgItems[index - 1];
        if (previousItem) {
          setProgrammaticHover(previousItem.name);
        } else {
          const lastItem = activePassiveOrderedOrgItems[activePassiveOrderedOrgItems.length - 1];
          if (lastItem) {
            setProgrammaticHover(lastItem.name);
          }
        }
      } else if (activePassiveOrderedOrgItems.length > 0) {
        setProgrammaticHover(activePassiveOrderedOrgItems[0].name);
      }
    } else if (code === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      if (programmaticHover) {
        const item = activePassiveOrderedOrgItems.find((i) => i.name === programmaticHover);
        if (item) {
          dispatch(setOrg(item.name));
          setIsOpen(false);
          setTimeout(() => {
            const el = document.querySelector(`.context-widget-org`);
            if (el) {
              (el as any).focus();
            }
          });
        }
      }
    }
  }

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => setIsOpen(open)}
      trigger={["click"]}
      content={() => {
        return (
          <div
            style={{
              minWidth: "500px",
              maxWidth: "500px",
              maxHeight: "calc(80svh - 82px)",
              overflow: "auto",
            }}
            className="py-1 pr-4 context-org-drawer"
            onKeyDown={handleKeyDown}
          >
            <div className="drawer-filter-section flex items-center gap-4 mb-4">
              <div className="font-bold">Orgs</div>
              <Input
                ref={filterRef}
                autoFocus
                value={orgSearch}
                onChange={(e) => setOrgSearch(e.target.value)}
                allowClear
                placeholder="Filter"
              />
            </div>
            <div className="flex flex-col">
              {IS_DEPLOYMENT_ENV_PROD ? null : (
                <div
                  className="border rounded px-4 py-2 flex flex-col gap-4 cursor-pointer org-item w-full"
                  onClick={() => {
                    dispatch(setOrg(""));
                    setIsOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">DEBUG - No Org</div>
                    {!!context.org ? null : <Tag color="green">Current</Tag>}
                  </div>
                </div>
              )}
              {orgItems
                .filter((org) => org.status?.active)
                .map((orgItem, index) => {
                  const isEven = index % 2 === 0;
                  const isCurrent = context.org === orgItem.name;

                  return (
                    <OrgItem
                      key={`org-${orgItem.name}`}
                      org={orgItem}
                      isEven={isEven}
                      isCurrent={isCurrent}
                      onDone={() => setIsOpen(false)}
                      programmaticHover={programmaticHover === orgItem.name}
                    />
                  );
                })}
              {orgItems
                .filter((org) => !org.status?.active)
                .map((orgItem, index) => {
                  const previousOrgCount = orgItems.filter((org) => org.status?.active).length;
                  const isEven = (previousOrgCount + index) % 2 === 0;
                  const isCurrent = context.org === orgItem.name;

                  return (
                    <OrgItem
                      key={`org-disabled-${orgItem.name}`}
                      org={orgItem}
                      isEven={isEven}
                      isCurrent={isCurrent}
                      isDeactive
                      onDone={() => setIsOpen(false)}
                      programmaticHover={programmaticHover === orgItem.name}
                    />
                  );
                })}
            </div>
          </div>
        );
      }}
    >
      {children}
    </Popover>
  );
};

interface OrgItemProps {
  isCurrent: boolean;
  isEven: boolean;
  isDeactive?: boolean;
  org: Org;
  onDone: any;
  programmaticHover: boolean;
}
function OrgItem({ isCurrent, isEven, isDeactive, org, onDone, programmaticHover }: OrgItemProps) {
  const dispatch = useAppDispatch();

  return (
    <a
      key={org.name}
      id={`org-item-${org.name}`}
      href={`${CONSOLE_URL}/console/org/${org.name}`}
      className={`${isCurrent ? "org-item-active" : ""} px-4 py-2 flex flex-col gap-4 cursor-pointer org-item ${
        programmaticHover ? "org-item-programmatic-hover" : ""
      } ${isEven ? "org-item-even" : "org-item-odd"} w-full`}
      onClick={(e) => {
        if (e.ctrlKey || e.metaKey || e.button === 1) {
          return;
        }
        e.preventDefault();
        dispatch(setOrg(org.name));
        onDone();
      }}
    >
      <div className="flex items-center justify-between">
        <div className="font-semibold">{org.name}</div>
        {isDeactive ? <Tag color="error">Inactive</Tag> : isCurrent ? <CheckOutlined /> : null}
      </div>
    </a>
  );
}
